import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  InputAdornment
} from '@mui/material';
import { AutoAwesomeOutlined, EditOutlined } from '@mui/icons-material';
import Header from '../components/Header';
import { ConfirmationDialog } from '../components';
import { useLoading } from '../providers';
import StringExistsDialog from '../components/StringExistsDialog';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../store';
import { clearResult, fetchAITranslation, reactivateTranslationAsync } from '../store/slices/aiTranslationSlice';
import { clearCreateRecord, createTranslationAndRefresh, refreshCurrentPage } from '../store/slices/translationsSlice';
import { getStorybookUrl } from '../utils/getStorybookUrl';
import { useAppSelector } from '../hooks/redux';
import { selectCreateError, selectCreateSuccess, selectCreateLoading as selectLoading } from '../store/selectors/translationsSelectors';
import { selectAiLoading, selectAiResult, selectHasTranslated, selectReactivateLoading, selectReactivateResult, selectReactivateError } from '../store/selectors/aiTranslationSelectors';
import { SUPPORT_EMAIL } from '../constants/supportConstants';
import { AITranslationStatus } from '../services/aiTranslationApi';


/**
 * Interface for navigation state when coming to AddTranslationScreen
 * Used to pre-populate form data and determine navigation context
 */
interface AddTranslationState {
  english?: string;     // Pre-populate English text field when reactivating from grid
  fromGrid?: boolean;   // Future use: Track navigation from grid for different UI behavior/validation
}

const AddTranslationScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as AddTranslationState;


  const [english, setEnglish] = useState(state?.english || '');
  const [spanish, setSpanish] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [modalVariant, setModalVariant] = useState<'deactivated' | 'active' | null>(null);
  const [activeTranslationId, setActiveTranslationId] = useState<string | null>(null);

  const dispatch = useDispatch<AppDispatch>();
  const createLoading = useAppSelector(selectLoading);
  const aiLoading = useAppSelector(selectAiLoading);
  const result = useAppSelector(selectAiResult);
  const hasTranslated = useAppSelector(selectHasTranslated);
  const reactivateLoading = useAppSelector(selectReactivateLoading);
  const reactivateResult = useAppSelector(selectReactivateResult);
  const reactivateError = useAppSelector(selectReactivateError);

  const createError = useAppSelector(selectCreateError);
  const createSuccess = useAppSelector(selectCreateSuccess);
  const loading = useLoading();


   useEffect(() => {
    if (createSuccess) {
      loading.stopLoading();
      navigate('/translations');
      clearTranslationState();
    } else if (createError) {
      loading.stopLoading();
      setEnglish('');
      setSpanish('');
      clearTranslationState();
    }
   }, [createSuccess, loading, createError]);
  
  const clearTranslationState = () => {
    dispatch(clearResult());       
    dispatch(clearCreateRecord()); 
  };
  
  const handleTranslate = () => {
    const text = english.trim();
    if (!text) return;
    dispatch(fetchAITranslation({ stringToTranslate: text, language: 'es' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    loading.showLoading();

    // If AI generated a translation and user hasn't edited it, mark as AI-generated
    const isAiGenerated = hasTranslated && result?.translatedString?.trim() === spanish.trim();

     await dispatch(createTranslationAndRefresh({
        language: 'es',
        originalText: english.trim(),
        translatedText: spanish.trim(),
        aiGenerated: isAiGenerated,
     }));
};

  const handleEnglishChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEnglish(value);

    if (hasTranslated && !aiLoading) {
      dispatch(clearResult());
      setSpanish('');
    }
  };

  const handleCancel = () => {
    navigate('/translations');
  };

  const handleEditEnglish = () => {
    setShowEditModal(true);
  };

  const handleConfirmEditEnglish = () => {
    setSpanish('');
    dispatch(clearResult());
    setShowEditModal(false);
  };

  const handleCancelEditEnglish = () => {
    setShowEditModal(false);
  };

const handleSubmitNew = () => {
    setModalVariant(null);
    setEnglish('');
    setSpanish('');
    dispatch(clearResult());
  };

  const handleGoToTranslation = () => {
    setModalVariant(null);
    navigate('/translations', { 
      state: { openDrawerId: activeTranslationId } 
    });
  };

  const handleReactivate = () => {
    dispatch(reactivateTranslationAsync({
      originalText: english.trim(),
      language: 'es'
    }));
  };

  useEffect(() => {
    if (!result) return;
    
    // Check if string was previously deactivated
    if (result.status === AITranslationStatus.DEACTIVATED) {
      setModalVariant('deactivated');
      return;
    }
    
    // Check if string is active (Published or NeedsReview)
    if (result.status === AITranslationStatus.PUBLISHED || result.status === AITranslationStatus.NEEDS_REVIEW || result.status === AITranslationStatus.NEW) {
      setActiveTranslationId(result.translationRecordId);
      setModalVariant('active');
      return;
    }
    
    // Normal flow - show translation (status is null = new string)
    if (result.translatedString) {
      setSpanish(result.translatedString);
    }
  }, [result]);

  useEffect(() => {
    return () => {
      dispatch(clearResult());
    };
  }, [dispatch]);

  useEffect(() => {
    if (reactivateResult) {
      setModalVariant(null);
      dispatch(refreshCurrentPage());
      navigate('/translations', { 
        state: { openDrawerId: reactivateResult.translationRecordId } 
      });
    }
  }, [reactivateResult, dispatch, navigate]);

  useEffect(() => {
    if (reactivateError) {
      setModalVariant(null);
    }
  }, [reactivateError]);

  return (
    <>
      <Box sx={styles.fullPageContainer}>
        <Header />

        <Box sx={styles.mainContent}>
          {/* Left Panel - Translation Form (70%) */}
          <Box sx={styles.leftPanel}>
            <Typography
              variant="h4"
              component="h1"
              sx={styles.pageTitle}
            >
              Submit New Translation
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={3}>
                {/* English Text Field */}
                <TextField
                  label="English"
                  multiline
                  rows={4}
                  value={english}
                  onChange={handleEnglishChange}
                  slotProps={{
                    htmlInput: { maxLength: 500 },
                    inputLabel: {
                      sx: { 
                        backgroundColor: 'white',
                        paddingLeft: '5px',
                        paddingRight: '5px',
                        marginLeft: '-5px'
                      }
                    },
                    input: hasTranslated ? {
                      endAdornment: (
                        <InputAdornment position="end" sx={styles.editIconAdornment}>
                          <IconButton
                            aria-label="edit english text"
                            onClick={handleEditEnglish}
                            edge="end"
                            size="small"
                          >
                            <EditOutlined />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }
                    : undefined,
                  }}
                  fullWidth
                  helperText="500 characters max"
                  disabled={aiLoading || hasTranslated}
                />

                {/* Translate Button */}
                <Box sx={styles.translateButtonContainer}>
                  <Button
                    variant={aiLoading ? 'contained' : 'outlined'}
                    startIcon={aiLoading ? <CircularProgress size={16} /> : <AutoAwesomeOutlined />}
                    onClick={handleTranslate}
                    disabled={aiLoading || !english.trim() || hasTranslated}
                  >
                    {aiLoading ? 'Translating' : 'Translate'}
                  </Button>
                </Box>

                {/* Spanish Translation Field */}
                <TextField
                  label="Spanish"
                  multiline
                  rows={4}
                  value={spanish}
                  onChange={(e) => setSpanish(e.target.value)}
                  slotProps={{ htmlInput: { maxLength: 500 } }}
                  fullWidth
                  helperText="500 characters max"
                  disabled={aiLoading || !hasTranslated}
                />

                {/* Action Buttons */}
                <Stack direction="row" spacing={2} sx={styles.actionButtons}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={
                      createLoading ||
                      aiLoading ||
                      !english.trim() ||
                      !spanish.trim()
                    }
                    size="large"
                  >
                    Submit
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    size="large"
                  >
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Box>

          {/* Right Panel - Control Panel (30%) */}
          <Box sx={styles.rightPanel}>
            <Stack spacing={3}>
              {/* Guidelines Section */}
              <Box>
                <Typography variant="body1" color="text.secondary" sx={styles.guidelinesHeading}>
                  Things to consider when submitting a new translation
                </Typography>

                <Box sx={styles.guidelinesText}>
                  <Typography variant="body1" component="div">
                    <ul>
                      <li>We currently use natural language keys to identify our English strings. This means that you cannot edit an English string once it has been submitted because that would break implementation.</li>
                      <li>If you want to submit a new English string, you must include the Spanish translation in your initial submission.</li>
                      <li>
                        See our content guidelines on{' '}
                        {getStorybookUrl() ? (
                          <a
                            href={getStorybookUrl()!}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={styles.linkStyleWithUnderline}
                          >
                            Storybook
                          </a>
                        ) : (
                          <span>Storybook</span>
                        )}
                        {' '}if you need help refining the voice & tone of your copy.
                      </li>
                    </ul>
                  </Typography>
                </Box>
              </Box>

              {/* Editing Section */}
              <Box>
                <Typography variant="body1" color="text.secondary" sx={styles.guidelinesHeading}>
                  How to edit an existing English string
                </Typography>

                <Typography variant="body1" sx={{ ...styles.guidelinesText, pl: 0 }}>
                  You cannot edit existing English strings in this tool. We suggest just submitting a new translation.
                </Typography>
              </Box>

              {/* Support Section */}
              <Box>
                <Typography variant="body2" sx={styles.guidelinesText}>
                  Having issues? Contact our support team at{' '}
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    style={styles.linkStyle}
                  >
                    {SUPPORT_EMAIL}
                  </a>
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>

      {/* Edit English Confirmation Modal */}
      <ConfirmationDialog
        open={showEditModal}
        onClose={handleCancelEditEnglish}
        onConfirm={handleConfirmEditEnglish}
        title="Are you sure you want to edit the English string?"
        message="Editing your English string will clear the generated translation."
        confirmText="Edit English"
        cancelText="Cancel"
        confirmColor="primary"
      />

      {modalVariant && (
        <StringExistsDialog
          open={true}
          variant={modalVariant}
          onSubmitNew={handleSubmitNew}
          onPrimaryAction={modalVariant === 'deactivated' ? handleReactivate : handleGoToTranslation}
          isLoading={modalVariant === 'deactivated' ? reactivateLoading : false}
        />
      )}
    </>
  );
};

// Styles object
const styles = {
  fullPageContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto', // Allow scrolling
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: 0,
    overflow: 'auto', // Allow scrolling
    minHeight: 0, // Important for flex containers to allow scrolling
    borderTop: '1px solid rgba(0, 0, 0, 0.23)'
  },
  leftPanel: {
    flex: { xs: 'none', md: '0 0 70%' },
    p: 4,
    borderRight: { xs: 'none', md: '1px solid rgba(0, 0, 0, 0.23)' },
    borderBottom: { xs: '1px solid rgba(0, 0, 0, 0.23)', md: 'none' },
    overflow: 'auto', // Allow scrolling in left panel
    '@media (max-width: 767px)': {
      p: 2, // Reduce padding on mobile for more space
    }
  },
  rightPanel: {
    flex: { xs: 'none', md: '0 0 30%' },
    bgcolor: 'grey.50',
    p: 4,
    overflow: 'auto', // Allow scrolling in right panel
    '@media (max-width: 767px)': {
      p: 2, // Reduce padding on mobile for more space
    }
  },
  pageTitle: {
    mb: 3,
    color: 'rgba(0, 0, 0, 0.87)',
    fontFamily: 'Roboto',
    fontSize: '34px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '123.5%',
    letterSpacing: '0.25px'
  },
  translateButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-start'
  },
  actionButtons: {
    pt: 2
  },
  guidelinesHeading: {
    fontWeight: 'bold'
  },
  guidelinesText: {
    p: 2,
    borderRadius: 1,
    color: 'rgba(0, 0, 0, 0.60)',
    '& ul': {
      margin: 0,
      paddingLeft: '1.5rem',
      fontSize: '1rem',
      lineHeight: 1.5
    },
    '& li': {
      fontSize: '1rem'
    }
  },
  sectionTitle: {
    mt: 3
  },
  editingText: {
    p: 2,
    bgcolor: 'grey.50',
    borderRadius: 1
  },
  linkStyle: {
    color: '#2196F3',
    textDecoration: 'none'
  },
  linkStyleWithUnderline: {
    color: '#2196F3',
    textDecoration: 'underline'
  },
  disabledLinkStyle: {
    color: 'rgba(0, 0, 0, 0.38)',
    textDecoration: 'none',
    fontStyle: 'italic'
  },
  editIconAdornment: {
    alignSelf: 'flex-start',
    marginTop: '8px'
  }
};

export default AddTranslationScreen;